import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Tag, Package, Settings as SettingsIcon, LogOut, Globe, TrendingUp, TrendingDown, Users, ShieldCheck, Target, Building2, ChefHat, FileSpreadsheet, Wallet, Landmark, Lock, ClipboardList } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import TenantSwitcher from "@/components/TenantSwitcher";
import TrialExpiredModal from "@/components/TrialExpiredModal";
import { ImpersonationBanner } from "@/pages/Admin";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PLAN_BADGES = {
  free:     { label: "Free",     bg: "bg-[var(--surface-hover)]", fg: "text-[var(--text-secondary)]" },
  basic:    { label: "Basic",    bg: "bg-[#3F5C7A]/15",            fg: "text-[#3F5C7A]" },
  pro:      { label: "Pro",      bg: "bg-[var(--brand-accent)]/15", fg: "text-[var(--brand-accent)]" },
  business: { label: "Business", bg: "bg-[#7A5C44]/15",            fg: "text-[#7A5C44]" },
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();

  const navItems = [
    { to: "/dashboard", label: t("dashboard"), icon: LayoutDashboard, testid: "nav-dashboard",
      desc: lang === "es" ? "Vista general del negocio" : "Business overview" },
    { to: "/invoices", label: t("invoices"), icon: TrendingDown, testid: "nav-invoices",
      desc: lang === "es" ? "Facturas de gastos + OCR" : "Expense invoices + OCR" },
    { to: "/ingresos", label: t("incomePage"), icon: TrendingUp, testid: "nav-income",
      desc: lang === "es" ? "Ventas y facturación" : "Sales and billing" },
    { to: "/salarios", label: t("salariesPage"), icon: Users, testid: "nav-salaries",
      desc: lang === "es" ? "Nóminas del equipo" : "Team payroll" },
    { to: "/products", label: t("products"), icon: Package, testid: "nav-products",
      desc: lang === "es" ? "Precios y variación" : "Prices and trends" },
    { to: "/inventario", label: lang === "es" ? "Inventario" : "Inventory", icon: ClipboardList, testid: "nav-inventory", feature: "inventory",
      desc: lang === "es" ? "Recuento mensual valorado" : "Monthly valued count" },
    { to: "/carta", label: lang === "es" ? "Carta" : "Menu", icon: ChefHat, testid: "nav-carta", feature: "menu",
      desc: lang === "es" ? "Escandallos y platos" : "Recipes and dishes" },
    { to: "/proveedores", label: t("vendors"), icon: Building2, testid: "nav-vendors",
      desc: lang === "es" ? "Compras por proveedor" : "Purchases by vendor" },
    { to: "/albaranes", label: lang === "es" ? "Albaranes" : "Delivery notes", icon: FileSpreadsheet, testid: "nav-albaranes", feature: "albaranes",
      desc: lang === "es" ? "Notas de entrega" : "Delivery notes" },
    { to: "/presupuesto", label: t("budget"), icon: Target, testid: "nav-budget", feature: "budget",
      desc: lang === "es" ? "Objetivos anuales" : "Annual targets" },
    { to: "/deudas", label: lang === "es" ? "Deudas Clientes" : "Client debts", icon: Wallet, testid: "nav-debts", feature: "client_debts",
      desc: lang === "es" ? "Cobros pendientes" : "Pending collections" },
    { to: "/tesoreria", label: lang === "es" ? "Tesorería" : "Treasury", icon: Landmark, testid: "nav-treasury", feature: "treasury", hideForViewer: true,
      desc: lang === "es" ? "Bancos y caja" : "Banks and cash" },
    { to: "/categories", label: t("categories"), icon: Tag, testid: "nav-categories", hideForViewer: true,
      desc: lang === "es" ? "Estructura contable" : "Accounting structure" },
    { to: "/settings", label: t("settings"), icon: SettingsIcon, testid: "nav-settings", hideForViewer: true,
      desc: lang === "es" ? "Plan y datos fiscales" : "Plan and tax data" },
  ];
  if (["master", "admin"].includes(user?.role)) {
    navItems.push({ to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin", hideForViewer: true,
      desc: lang === "es" ? "Panel Master · usuarios" : "Master panel · users" });
  }
  // Iter 80 — Salarios oculto para viewers (además del sidebar en la
  // categoría existente, hay entrada de nav). Buscamos "/salarios" y lo
  // marcamos como hideForViewer si existe.
  const salariosItem = navItems.find((n) => n.to === "/salarios");
  if (salariosItem) salariosItem.hideForViewer = true;

  // Iter 63 — plan gating. Items with a `feature` key are only fully clickable
  // when `user.plan_features[feature]` is true. Otherwise render them dimmed
  // with a lock icon; the click still routes to the paywall page (FeatureGate).
  const isPrivileged = ["master", "admin"].includes(user?.role);
  const isEnabled = (item) => {
    if (!item.feature) return true;
    if (isPrivileged) return true;
    return !!(user?.plan_features && user.plan_features[item.feature]);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex">
      {/* Iter 80 — Blocking modal when the 30-day trial has expired. */}
      <TrialExpiredModal user={user} lang={lang} />
      {/* Left sidebar (desktop) */}
      <aside
        className="hidden md:flex flex-col w-60 shrink-0 border-r border-[var(--border-color)] bg-[var(--surface)] sticky top-0 h-screen z-30"
        data-testid="sidebar"
      >
        <div className="px-5 py-5 flex items-center gap-2 border-b border-[var(--border-color)]">
          <Logo size={32} />
          <span className="font-display font-bold text-base leading-tight">
            ChefSync <span className="text-[var(--text-muted)] font-normal">P&amp;L</span>
          </span>
        </div>
        {/* iter 64 — multi-tenant switcher */}
        <div className="px-2 pt-2 pb-1 border-b border-[var(--border-color)]">
          <TenantSwitcher />
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {navItems.filter((item) => !(item.hideForViewer && user?.team_role === "viewer")).map((item) => {
            const enabled = isEnabled(item);
            const lockTitle = lang === "es" ? "Requiere plan Pro" : "Requires Pro plan";
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                title={enabled ? item.desc : `${item.desc || ""}${item.desc ? " · " : ""}${lockTitle}`}
                className={({ isActive }) =>
                  `flex items-start gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-[var(--surface-hover)] text-[var(--text-primary)] font-medium"
                      : enabled
                        ? "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]"
                        : "text-[var(--text-muted)] opacity-60 hover:opacity-80 hover:bg-[var(--surface-hover)]"
                  }`
                }
              >
                <item.icon size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block leading-tight">{item.label}</span>
                  {item.desc && (
                    <span className="block text-[10px] text-[var(--text-muted)] leading-tight mt-0.5 truncate">
                      {item.desc}
                    </span>
                  )}
                </span>
                {!enabled && (
                  <Lock
                    size={12}
                    strokeWidth={2}
                    className="text-[var(--text-muted)] mt-1 shrink-0"
                    data-testid={`nav-lock-${item.feature}`}
                  />
                )}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
      <header className="glass-nav sticky top-0 z-40">
        <div className="px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="md:hidden flex items-center gap-2">
            <Logo size={28} />
            <span className="font-display font-bold text-base">ChefSync <span className="text-[var(--text-muted)] font-normal">P&amp;L</span></span>
          </div>
          <div className="hidden md:block" />

          <div className="flex items-center gap-2">
            {user?.role === "master" ? (
              <span
                data-testid="role-badge-master"
                className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-[var(--brand-accent)]/20 text-[var(--brand-accent)] hidden sm:inline-flex items-center gap-1"
                title="Master / SaaS owner"
              >
                <ShieldCheck size={10} /> Master
              </span>
            ) : user?.is_trialing ? (
              <button
                data-testid="trial-badge"
                onClick={() => navigate("/settings")}
                className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-[var(--brand-accent)]/20 text-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/30 hidden sm:inline-flex items-center gap-1 transition-colors"
                title={`Trial Business · ${user.trial_days_left} days`}
              >
                🎁 Trial · {user.trial_days_left}d
              </button>
            ) : (() => {
              const plan = user?.plan || "free";
              const badge = PLAN_BADGES[plan] || PLAN_BADGES.free;
              const isPaid = plan !== "free";
              const cls = `text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${badge.bg} ${badge.fg} hidden sm:inline-flex items-center gap-1`;
              return isPaid ? (
                <span
                  data-testid={`plan-badge-${plan}`}
                  className={cls}
                  title={t("currentPlan") + ": " + badge.label}
                >
                  {badge.label}
                </span>
              ) : (
                <button
                  data-testid="plan-badge-free"
                  onClick={() => navigate("/settings")}
                  className={`${cls} hover:bg-[var(--border-color)] transition-colors`}
                  title={t("upgrade")}
                >
                  {badge.label}
                </button>
              );
            })()}
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="flex items-center gap-1.5 text-xs uppercase tracking-wider px-2.5 py-1.5 rounded-md text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] transition-colors"
            >
              <Globe size={14} strokeWidth={1.75} />
              <span className={lang === "es" ? "font-semibold text-[var(--text-primary)]" : ""}>ES</span>
              <span className="opacity-40">|</span>
              <span className={lang === "en" ? "font-semibold text-[var(--text-primary)]" : ""}>EN</span>
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="user-menu-trigger"
                  className="gap-2 font-normal"
                >
                  <div className="w-7 h-7 rounded-full bg-[var(--primary)] text-white grid place-items-center text-xs font-semibold">
                    {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-xs text-[var(--text-muted)]">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <SettingsIcon size={14} className="mr-2" />
                  {t("settings")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut size={14} className="mr-2" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden border-t border-[var(--border-color)] px-4 py-2 flex items-center gap-1 overflow-x-auto">
          {navItems.filter((item) => !(item.hideForViewer && user?.team_role === "viewer")).map((item) => {
            const enabled = isEnabled(item);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                title={item.desc}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap ${
                    isActive
                      ? "bg-[var(--surface-hover)] font-medium"
                      : enabled
                        ? "text-[var(--text-secondary)]"
                        : "text-[var(--text-muted)] opacity-60"
                  }`
                }
              >
                <item.icon size={14} />
                {item.label}
                {!enabled && <Lock size={10} strokeWidth={2} />}
              </NavLink>
            );
          })}
        </nav>
      </header>

      <ImpersonationBanner />

      <main className="flex-1 w-full px-6 lg:px-10 py-8 animate-fade-up">
        <Outlet />
      </main>

      <footer className="border-t border-[var(--border-color)] py-6 mt-12">
        <div className="px-6 lg:px-10 text-xs text-[var(--text-muted)] flex items-center justify-between">
          <span>© {new Date().getFullYear()} ChefSync P&amp;L</span>
          <span className="font-mono-num">v0.1</span>
        </div>
      </footer>
      </div>
    </div>
  );
}
